export interface FoodSpecOption {
  name: string;
  price: number;
}

export interface FoodSpec {
  label: string;
  options: FoodSpecOption[];
}

export interface Food {
  id: string;
  name: string;
  category: string;
  categoryId: string;
  price: number;
  rating: number;
  sales: number;
  distance: number;
  image: string;
  gallery: string[];
  short: string;
  description: string;
  tags: string[];
  specs: FoodSpec[];
  restaurant: {
    name: string;
    phone: string;
    deliveryTime: string;
    distance: string;
  };
  nutrition: { label: string; value: string }[];
}

export const foodCategories = [
  { id: "cn", name: "中餐", icon: "ri-restaurant-2-line", desc: "家常小炒、地道面食与广式点心，锅气十足" },
  { id: "western", name: "西餐", icon: "ri-restaurant-line", desc: "意面、牛排与手作披萨，用橄榄油与香草打底" },
  { id: "dessert", name: "甜点", icon: "ri-cake-2-line", desc: "现烤蛋糕与法式布丁，甜而不腻的收尾" },
  { id: "drink", name: "饮品", icon: "ri-cup-line", desc: "鲜果气泡水与手作奶盖，清爽解腻" },
];

export const foods: Food[] = [
  {
    id: "beef-noodle",
    name: "招牌红烧牛肉面",
    category: "中餐",
    categoryId: "cn",
    price: 32,
    rating: 4.8,
    sales: 2380,
    distance: 1.2,
    image:
      "https://readdy.ai/api/search-image?query=top%20down%20bowl%20of%20braised%20beef%20noodle%20soup%20with%20tender%20chunks%20of%20beef%20shank%20and%20bok%20choy%20on%20a%20warm%20ceramic%20bowl%2C%20soft%20natural%20window%20light%2C%20warm%20neutral%20linen%20backdrop%2C%20shallow%20depth%20of%20field%2C%20appetizing%20editorial%20food%20photography%2C%20high%20detail&width=800&height=600&seq=food-beef-noodle-01&orientation=landscape",
    gallery: [
      "https://readdy.ai/api/search-image?query=top%20down%20bowl%20of%20braised%20beef%20noodle%20soup%20with%20tender%20chunks%20of%20beef%20shank%20and%20bok%20choy%20on%20a%20warm%20ceramic%20bowl%2C%20soft%20natural%20window%20light%2C%20warm%20neutral%20linen%20backdrop%2C%20shallow%20depth%20of%20field%2C%20appetizing%20editorial%20food%20photography%2C%20high%20detail&width=800&height=600&seq=food-beef-noodle-01&orientation=landscape",
      "https://readdy.ai/api/search-image?query=close%20up%20steaming%20braised%20beef%20noodle%20in%20warm%20broth%20pulled%20with%20chopsticks%2C%20rich%20aroma%2C%20soft%20window%20light%2C%20warm%20cream%20table%20surface%2C%20editorial%20food%20photography%2C%20high%20detail&width=800&height=600&seq=food-beef-noodle-02&orientation=landscape",
      "https://readdy.ai/api/search-image?query=side%20view%20of%20a%20rustic%20ceramic%20bowl%20of%20red%20braised%20beef%20noodles%20with%20cilantro%20and%20chili%20oil%2C%20warm%20ambient%20light%2C%20neutral%20beige%20backdrop%2C%20editorial%20food%20photography%2C%20high%20detail&width=800&height=600&seq=food-beef-noodle-03&orientation=landscape",
    ],
    short: "慢炖三小时的牛腩，浓郁酱香配上手打小麦面",
    description:
      "精选澳洲牛腩，以八角、桂皮与豆瓣慢炖三小时，肉质软糯入味；汤底用牛骨与番茄熬至琥珀色，配上手打小麦面，筋道挂汤。每一口都是热腾腾的满足。",
    tags: ["招牌", "热卖", "牛腩"],
    specs: [
      { label: "辣度", options: [{ name: "微辣", price: 0 }, { name: "中辣", price: 0 }, { name: "特辣", price: 0 }] },
      { label: "加料", options: [{ name: "不加料", price: 0 }, { name: "加牛腩", price: 12 }, { name: "加溏心蛋", price: 5 }] },
    ],
    restaurant: { name: "巷口面馆 · 建国路店", phone: "400-880-1234", deliveryTime: "约 28 分钟", distance: "距您 1.2km" },
    nutrition: [
      { label: "热量", value: "620 kcal" },
      { label: "蛋白质", value: "32 g" },
      { label: "碳水", value: "72 g" },
      { label: "脂肪", value: "18 g" },
    ],
  },
  {
    id: "kungpao-chicken",
    name: "秘制宫保鸡丁",
    category: "中餐",
    categoryId: "cn",
    price: 38,
    rating: 4.7,
    sales: 1890,
    distance: 1.6,
    image:
      "https://readdy.ai/api/search-image?query=top%20down%20plate%20of%20kung%20pao%20chicken%20with%20peanuts%20and%20dried%20chili%20on%20a%20white%20ceramic%20plate%2C%20soft%20natural%20light%2C%20warm%20neutral%20linen%20backdrop%2C%20shallow%20depth%20of%20field%2C%20appetizing%20editorial%20food%20photography%2C%20high%20detail&width=800&height=600&seq=food-kungpao-01&orientation=landscape",
    gallery: [
      "https://readdy.ai/api/search-image?query=top%20down%20plate%20of%20kung%20pao%20chicken%20with%20peanuts%20and%20dried%20chili%20on%20a%20white%20ceramic%20plate%2C%20soft%20natural%20light%2C%20warm%20neutral%20linen%20backdrop%2C%20shallow%20depth%20of%20field%2C%20appetizing%20editorial%20food%20photography%2C%20high%20detail&width=800&height=600&seq=food-kungpao-01&orientation=landscape",
      "https://readdy.ai/api/search-image?query=close%20up%20of%20glossy%20kung%20pao%20chicken%20cubes%20with%20roasted%20peanuts%2C%20warm%20light%2C%20cream%20table%20surface%2C%20editorial%20food%20photography%2C%20high%20detail&width=800&height=600&seq=food-kungpao-02&orientation=landscape",
      "https://readdy.ai/api/search-image?query=rustic%20bowl%20of%20sichuan%20kung%20pao%20chicken%20with%20spring%20onion%2C%20warm%20ambient%20light%2C%20beige%20backdrop%2C%20editorial%20food%20photography%2C%20high%20detail&width=800&height=600&seq=food-kungpao-03&orientation=landscape",
    ],
    short: "荔枝口微甜，花生脆香，川味经典的下饭神器",
    description:
      "鸡腿肉切丁上浆滑炒，配干辣椒与花椒爆香，淋上荔枝口微甜酱汁，最后撒入油炸花生与葱段。酸甜微辣，鸡肉滑嫩，是川菜里最下饭的那一口。",
    tags: ["川味", "下饭", "花生"],
    specs: [
      { label: "辣度", options: [{ name: "微辣", price: 0 }, { name: "中辣", price: 0 }, { name: "重辣", price: 0 }] },
      { label: "主食", options: [{ name: "不加米饭", price: 0 }, { name: "加米饭", price: 3 }] },
    ],
    restaurant: { name: "蜀香小馆 · 美食街店", phone: "400-880-1268", deliveryTime: "约 32 分钟", distance: "距您 1.6km" },
    nutrition: [
      { label: "热量", value: "480 kcal" },
      { label: "蛋白质", value: "28 g" },
      { label: "碳水", value: "26 g" },
      { label: "脂肪", value: "22 g" },
    ],
  },
  {
    id: "shrimp-dumpling",
    name: "粤式虾饺皇",
    category: "中餐",
    categoryId: "cn",
    price: 28,
    rating: 4.9,
    sales: 1520,
    distance: 2.1,
    image:
      "https://readdy.ai/api/search-image?query=steamed%20cantonese%20shrimp%20dumplings%20in%20a%20bamboo%20steamer%20basket%2C%20translucent%20wrapper%20showing%20whole%20shrimp%2C%20soft%20natural%20light%2C%20warm%20neutral%20linen%20backdrop%2C%20editorial%20dim%20sum%20photography%2C%20high%20detail&width=800&height=600&seq=food-shrimp-dumpling-01&orientation=landscape",
    gallery: [
      "https://readdy.ai/api/search-image?query=steamed%20cantonese%20shrimp%20dumplings%20in%20a%20bamboo%20steamer%20basket%2C%20translucent%20wrapper%20showing%20whole%20shrimp%2C%20soft%20natural%20light%2C%20warm%20neutral%20linen%20backdrop%2C%20editorial%20dim%20sum%20photography%2C%20high%20detail&width=800&height=600&seq=food-shrimp-dumpling-01&orientation=landscape",
      "https://readdy.ai/api/search-image?query=close%20up%20of%20plump%20shrimp%20har%20gow%20dumplings%20with%20golden%20translucent%20skin%2C%20warm%20light%2C%20cream%20table%20surface%2C%20editorial%20food%20photography%2C%20high%20detail&width=800&height=600&seq=food-shrimp-dumpling-02&orientation=landscape",
      "https://readdy.ai/api/search-image?query=dim%20sum%20platter%20with%20shrimp%20dumplings%20and%20soy%20sauce%20dip%2C%20warm%20ambient%20light%2C%20beige%20backdrop%2C%20editorial%20food%20photography%2C%20high%20detail&width=800&height=600&seq=food-shrimp-dumpling-03&orientation=landscape",
    ],
    short: "整只鲜虾现包，薄如蝉翼的水晶皮一口爆汁",
    description:
      "每一只虾饺都用整只船冻鲜虾手工包制，水晶皮薄而透亮，蒸好后晶莹饱满。咬开先是弹牙的虾肉，再是清甜汁水，是广式早茶里最讲究的那一笼。",
    tags: ["粤式", "早茶", "现包"],
    specs: [
      { label: "份量", options: [{ name: "4 只装", price: 0 }, { name: "8 只装", price: 24 }] },
      { label: "蘸料", options: [{ name: "姜醋汁", price: 0 }, { name: "辣椒油", price: 0 }] },
    ],
    restaurant: { name: "第壹笼 · 早茶铺", phone: "400-880-1290", deliveryTime: "约 38 分钟", distance: "距您 2.1km" },
    nutrition: [
      { label: "热量", value: "260 kcal" },
      { label: "蛋白质", value: "18 g" },
      { label: "碳水", value: "24 g" },
      { label: "脂肪", value: "8 g" },
    ],
  },
  {
    id: "malaxiangguo",
    name: "麻辣香锅（自选）",
    category: "中餐",
    categoryId: "cn",
    price: 58,
    rating: 4.6,
    sales: 1340,
    distance: 2.8,
    image:
      "https://readdy.ai/api/search-image?query=top%20down%20of%20spicy%20sichuan%20malaxiangguo%20stir%20fry%20with%20vegetables%20tofu%20and%20meat%20in%20a%20black%20cast%20iron%20pan%2C%20red%20chili%20oil%2C%20soft%20natural%20light%2C%20warm%20neutral%20backdrop%2C%20editorial%20food%20photography%2C%20high%20detail&width=800&height=600&seq=food-malaxiangguo-01&orientation=landscape",
    gallery: [
      "https://readdy.ai/api/search-image?query=top%20down%20of%20spicy%20sichuan%20malaxiangguo%20stir%20fry%20with%20vegetables%20tofu%20and%20meat%20in%20a%20black%20cast%20iron%20pan%2C%20red%20chili%20oil%2C%20soft%20natural%20light%2C%20warm%20neutral%20backdrop%2C%20editorial%20food%20photography%2C%20high%20detail&width=800&height=600&seq=food-malaxiangguo-01&orientation=landscape",
      "https://readdy.ai/api/search-image?query=close%20up%20of%20glossy%20spicy%20stir%20fry%20with%20lotus%20root%20and%20sliced%20beef%2C%20warm%20light%2C%20cream%20table%20surface%2C%20editorial%20food%20photography%2C%20high%20detail&width=800&height=600&seq=food-malaxiangguo-02&orientation=landscape",
      "https://readdy.ai/api/search-image?query=steaming%20bowl%20of%20malatang%20style%20spicy%20mix%20with%20sesame%20sprinkles%2C%20warm%20ambient%20light%2C%20beige%20backdrop%2C%20editorial%20food%20photography%2C%20high%20detail&width=800&height=600&seq=food-malaxiangguo-03&orientation=landscape",
    ],
    short: "五十余种配菜任选，香辣过瘾的干锅风味",
    description:
      "五十余种荤素配菜任你挑选，土豆片、宽粉、牛肉、藕片、豆皮……先炸后炒，锁住食材本味，再以二十余味香料爆炒，麻辣鲜香，越吃越上头。",
    tags: ["自选", "麻辣", "聚餐"],
    specs: [
      { label: "辣度", options: [{ name: "微辣", price: 0 }, { name: "中辣", price: 0 }, { name: "变态辣", price: 0 }] },
      { label: "份量", options: [{ name: "单人份", price: 0 }, { name: "双人份", price: 38 }] },
    ],
    restaurant: { name: "炒锅先生 · 香锅工坊", phone: "400-880-1310", deliveryTime: "约 40 分钟", distance: "距您 2.8km" },
    nutrition: [
      { label: "热量", value: "760 kcal" },
      { label: "蛋白质", value: "34 g" },
      { label: "碳水", value: "48 g" },
      { label: "脂肪", value: "42 g" },
    ],
  },
  {
    id: "charsiu-rice",
    name: "广式蜜汁叉烧饭",
    category: "中餐",
    categoryId: "cn",
    price: 36,
    rating: 4.7,
    sales: 1760,
    distance: 1.9,
    image:
      "https://readdy.ai/api/search-image?query=top%20down%20cantonese%20bbq%20pork%20charsiu%20rice%20bowl%20with%20glossy%20honey%20glaze%20and%20bok%20choy%2C%20soft%20natural%20light%2C%20warm%20neutral%20linen%20backdrop%2C%20editorial%20food%20photography%2C%20high%20detail&width=800&height=600&seq=food-charsiu-rice-01&orientation=landscape",
    gallery: [
      "https://readdy.ai/api/search-image?query=top%20down%20cantonese%20bbq%20pork%20charsiu%20rice%20bowl%20with%20glossy%20honey%20glaze%20and%20bok%20choy%2C%20soft%20natural%20light%2C%20warm%20neutral%20linen%20backdrop%2C%20editorial%20food%20photography%2C%20high%20detail&width=800&height=600&seq=food-charsiu-rice-01&orientation=landscape",
      "https://readdy.ai/api/search-image?query=close%20up%20of%20sliced%20honey%20glazed%20bbq%20pork%20with%20charred%20edges%2C%20warm%20light%2C%20cream%20table%20surface%2C%20editorial%20food%20photography%2C%20high%20detail&width=800&height=600&seq=food-charsiu-rice-02&orientation=landscape",
      "https://readdy.ai/api/search-image?query=rice%20plate%20with%20cantonese%20roast%20pork%20and%20green%20vegetables%2C%20warm%20ambient%20light%2C%20beige%20backdrop%2C%20editorial%20food%20photography%2C%20high%20detail&width=800&height=600&seq=food-charsiu-rice-03&orientation=landscape",
    ],
    short: "蜜汁焦香，肥瘦相间的叉烧配丝苗米饭",
    description:
      "选用梅头肉腌制过夜，炭火烤至外层微焦、内里多汁，刷上麦芽糖蜜汁，甜咸交织。配上粒粒分明的丝苗米饭与一碟时蔬，是最经典的港式滋味。",
    tags: ["港式", "叉烧", "套餐"],
    specs: [
      { label: "肥瘦", options: [{ name: "半肥瘦", price: 0 }, { name: "全瘦", price: 0 }] },
      { label: "加料", options: [{ name: "不加", price: 0 }, { name: "加煎蛋", price: 4 }, { name: "加叉烧", price: 14 }] },
    ],
    restaurant: { name: "烧腊世家 · 港味餐室", phone: "400-880-1333", deliveryTime: "约 30 分钟", distance: "距您 1.9km" },
    nutrition: [
      { label: "热量", value: "680 kcal" },
      { label: "蛋白质", value: "30 g" },
      { label: "碳水", value: "78 g" },
      { label: "脂肪", value: "22 g" },
    ],
  },
  {
    id: "truffle-pasta",
    name: "黑松露菌菇意面",
    category: "西餐",
    categoryId: "western",
    price: 68,
    rating: 4.8,
    sales: 980,
    distance: 3.2,
    image:
      "https://readdy.ai/api/search-image?query=top%20down%20plate%20of%20creamy%20truffle%20mushroom%20pasta%20with%20shaved%20black%20truffle%20and%20parmesan%2C%20elegant%20white%20plate%2C%20soft%20natural%20light%2C%20warm%20neutral%20linen%20backdrop%2C%20editorial%20food%20photography%2C%20high%20detail&width=800&height=600&seq=food-truffle-pasta-01&orientation=landscape",
    gallery: [
      "https://readdy.ai/api/search-image?query=top%20down%20plate%20of%20creamy%20truffle%20mushroom%20pasta%20with%20shaved%20black%20truffle%20and%20parmesan%2C%20elegant%20white%20plate%2C%20soft%20natural%20light%2C%20warm%20neutral%20linen%20backdrop%2C%20editorial%20food%20photography%2C%20high%20detail&width=800&height=600&seq=food-truffle-pasta-01&orientation=landscape",
      "https://readdy.ai/api/search-image?query=close%20up%20of%20twirled%20fettuccine%20with%20cream%20sauce%20and%20mushrooms%2C%20warm%20light%2C%20cream%20table%20surface%2C%20editorial%20food%20photography%2C%20high%20detail&width=800&height=600&seq=food-truffle-pasta-02&orientation=landscape",
      "https://readdy.ai/api/search-image?query=rustic%20pasta%20dish%20with%20black%20truffle%20shavings%20and%20fresh%20herbs%2C%20warm%20ambient%20light%2C%20beige%20backdrop%2C%20editorial%20food%20photography%2C%20high%20detail&width=800&height=600&seq=food-truffle-pasta-03&orientation=landscape",
    ],
    short: "现刨黑松露，奶油菌菇酱包裹每一根意面",
    description:
      "意大利进口 fettuccine 意面煮至 al dente，拌入用牛肝菌与淡奶油熬成的酱汁，出锅前现刨黑松露与帕玛森芝士，香气层层递进，是西餐里的高级感代表。",
    tags: ["意式", "松露", "招牌"],
    specs: [
      { label: "面条", options: [{ name: "宽面", price: 0 }, { name: "细面", price: 0 }, { name: "通心粉", price: 0 }] },
      { label: "加料", options: [{ name: "原味", price: 0 }, { name: "加鸡胸", price: 16 }, { name: "加虾仁", price: 22 }] },
    ],
    restaurant: { name: "Oliva 橄榄小馆", phone: "400-880-1356", deliveryTime: "约 42 分钟", distance: "距您 3.2km" },
    nutrition: [
      { label: "热量", value: "720 kcal" },
      { label: "蛋白质", value: "22 g" },
      { label: "碳水", value: "68 g" },
      { label: "脂肪", value: "38 g" },
    ],
  },
  {
    id: "cheese-burger",
    name: "经典双层芝士汉堡",
    category: "西餐",
    categoryId: "western",
    price: 45,
    rating: 4.6,
    sales: 2210,
    distance: 0.9,
    image:
      "https://readdy.ai/api/search-image?query=double%20cheeseburger%20with%20melted%20cheddar%20lettuce%20and%20tomato%20on%20a%20toasted%20brioche%20bun%2C%20soft%20natural%20light%2C%20warm%20neutral%20linen%20backdrop%2C%20editorial%20food%20photography%2C%20high%20detail&width=800&height=600&seq=food-cheese-burger-01&orientation=landscape",
    gallery: [
      "https://readdy.ai/api/search-image?query=double%20cheeseburger%20with%20melted%20cheddar%20lettuce%20and%20tomato%20on%20a%20toasted%20brioche%20bun%2C%20soft%20natural%20light%2C%20warm%20neutral%20linen%20backdrop%2C%20editorial%20food%20photography%2C%20high%20detail&width=800&height=600&seq=food-cheese-burger-01&orientation=landscape",
      "https://readdy.ai/api/search-image?query=close%20up%20of%20juicy%20beef%20patty%20with%20dripping%20cheese%2C%20warm%20light%2C%20cream%20table%20surface%2C%20editorial%20food%20photography%2C%20high%20detail&width=800&height=600&seq=food-cheese-burger-02&orientation=landscape",
      "https://readdy.ai/api/search-image?query=burger%20with%20crispy%20fries%20on%20a%20wooden%20board%2C%20warm%20ambient%20light%2C%20beige%20backdrop%2C%20editorial%20food%20photography%2C%20high%20detail&width=800&height=600&seq=food-cheese-burger-03&orientation=landscape",
    ],
    short: "现煎安格斯牛肉饼，双层芝士拉丝满足",
    description:
      "安格斯牛肉手工压制，现点现煎，外层焦香内里多汁；双层车打芝士在热力下融化拉丝，配新鲜生菜、番茄与秘制酱汁，夹入黄油烤过的布里欧修面包，一口爆汁。",
    tags: ["汉堡", "芝士", "人气"],
    specs: [
      { label: "牛肉", options: [{ name: "标准", price: 0 }, { name: "双层加量", price: 14 }] },
      { label: "配餐", options: [{ name: "不加", price: 0 }, { name: "加薯条", price: 12 }, { name: "加洋葱圈", price: 15 }] },
    ],
    restaurant: { name: "Bun & Co. 汉堡工坊", phone: "400-880-1378", deliveryTime: "约 26 分钟", distance: "距您 0.9km" },
    nutrition: [
      { label: "热量", value: "820 kcal" },
      { label: "蛋白质", value: "38 g" },
      { label: "碳水", value: "52 g" },
      { label: "脂肪", value: "46 g" },
    ],
  },
  {
    id: "salmon-steak",
    name: "香煎三文鱼配芦笋",
    category: "西餐",
    categoryId: "western",
    price: 88,
    rating: 4.9,
    sales: 760,
    distance: 3.8,
    image:
      "https://readdy.ai/api/search-image?query=pan%20seared%20salmon%20fillet%20with%20crispy%20skin%20and%20grilled%20asparagus%20on%20a%20white%20plate%2C%20lemon%20wedge%2C%20soft%20natural%20light%2C%20warm%20neutral%20linen%20backdrop%2C%20editorial%20food%20photography%2C%20high%20detail&width=800&height=600&seq=food-salmon-steak-01&orientation=landscape",
    gallery: [
      "https://readdy.ai/api/search-image?query=pan%20seared%20salmon%20fillet%20with%20crispy%20skin%20and%20grilled%20asparagus%20on%20a%20white%20plate%2C%20lemon%20wedge%2C%20soft%20natural%20light%2C%20warm%20neutral%20linen%20backdrop%2C%20editorial%20food%20photography%2C%20high%20detail&width=800&height=600&seq=food-salmon-steak-01&orientation=landscape",
      "https://readdy.ai/api/search-image?query=close%20up%20of%20crispy%20skin%20salmon%20with%20herb%20butter%2C%20warm%20light%2C%20cream%20table%20surface%2C%20editorial%20food%20photography%2C%20high%20detail&width=800&height=600&seq=food-salmon-steak-02&orientation=landscape",
      "https://readdy.ai/api/search-image?query=salmon%20dish%20with%20asparagus%20and%20cherry%20tomatoes%2C%20warm%20ambient%20light%2C%20beige%20backdrop%2C%20editorial%20food%20photography%2C%20high%20detail&width=800&height=600&seq=food-salmon-steak-03&orientation=landscape",
    ],
    short: "挪威三文鱼，鱼皮煎到酥脆，内里嫩滑多汁",
    description:
      "选用挪威空运三文鱼中段，鱼皮朝下煎至金黄酥脆，淋上柠檬黄油汁，搭配炭烤芦笋与樱桃番茄。外酥里嫩，清爽不腻，是轻食与健身人群的心头好。",
    tags: ["轻食", "三文鱼", "高蛋白"],
    specs: [
      { label: "熟度", options: [{ name: "五分熟", price: 0 }, { name: "七分熟", price: 0 }, { name: "全熟", price: 0 }] },
      { label: "配餐", options: [{ name: "芦笋", price: 0 }, { name: "藜麦沙拉", price: 8 }, { name: "烤时蔬", price: 6 }] },
    ],
    restaurant: { name: "The Dock 海鲜餐厅", phone: "400-880-1399", deliveryTime: "约 45 分钟", distance: "距您 3.8km" },
    nutrition: [
      { label: "热量", value: "420 kcal" },
      { label: "蛋白质", value: "40 g" },
      { label: "碳水", value: "12 g" },
      { label: "脂肪", value: "24 g" },
    ],
  },
  {
    id: "margherita-pizza",
    name: "玛格丽特披萨",
    category: "西餐",
    categoryId: "western",
    price: 56,
    rating: 4.5,
    sales: 1180,
    distance: 2.4,
    image:
      "https://readdy.ai/api/search-image?query=top%20down%20wood%20fired%20margherita%20pizza%20with%20fresh%20basil%20and%20melted%20mozzarella%20on%20a%20rustic%20wooden%20board%2C%20soft%20natural%20light%2C%20warm%20neutral%20backdrop%2C%20editorial%20food%20photography%2C%20high%20detail&width=800&height=600&seq=food-pizza-01&orientation=landscape",
    gallery: [
      "https://readdy.ai/api/search-image?query=top%20down%20wood%20fired%20margherita%20pizza%20with%20fresh%20basil%20and%20melted%20mozzarella%20on%20a%20rustic%20wooden%20board%2C%20soft%20natural%20light%2C%20warm%20neutral%20backdrop%2C%20editorial%20food%20photography%2C%20high%20detail&width=800&height=600&seq=food-pizza-01&orientation=landscape",
      "https://readdy.ai/api/search-image?query=close%20up%20of%20a%20slice%20of%20margherita%20pizza%20being%20lifted%20with%20stretchy%20cheese%2C%20warm%20light%2C%20cream%20table%20surface%2C%20editorial%20food%20photography%2C%20high%20detail&width=800&height=600&seq=food-pizza-02&orientation=landscape",
      "https://readdy.ai/api/search-image?query=whole%20neapolitan%20pizza%20on%20a%20metal%20tray%20with%20basil%20leaves%2C%20warm%20ambient%20light%2C%20beige%20backdrop%2C%20editorial%20food%20photography%2C%20high%20detail&width=800&height=600&seq=food-pizza-03&orientation=landscape",
    ],
    short: "柴火烤制的拿坡里披萨，番茄与罗勒的经典组合",
    description:
      "手工揉制的面团低温发酵 24 小时，抹上圣马扎诺番茄酱，铺满水牛马苏里拉，入柴火炉 90 秒高温烤制。饼边蓬松焦香，番茄酸甜、罗勒清香，最纯粹的意式风味。",
    tags: ["披萨", "柴火", "经典"],
    specs: [
      { label: "尺寸", options: [{ name: "9 寸", price: 0 }, { name: "12 寸", price: 22 }] },
      { label: "加料", options: [{ name: "原味", price: 0 }, { name: "加帕尔玛火腿", price: 20 }, { name: "加倍芝士", price: 10 }] },
    ],
    restaurant: { name: "Forno 柴火披萨", phone: "400-880-1420", deliveryTime: "约 36 分钟", distance: "距您 2.4km" },
    nutrition: [
      { label: "热量", value: "690 kcal" },
      { label: "蛋白质", value: "26 g" },
      { label: "碳水", value: "82 g" },
      { label: "脂肪", value: "28 g" },
    ],
  },
  {
    id: "creme-brulee",
    name: "法式焦糖布丁",
    category: "甜点",
    categoryId: "dessert",
    price: 26,
    rating: 4.8,
    sales: 1640,
    distance: 1.4,
    image:
      "https://readdy.ai/api/search-image?query=creme%20brulee%20in%20a%20white%20ramekin%20with%20cracked%20caramelized%20sugar%20crust%20and%20berries%2C%20soft%20natural%20light%2C%20warm%20neutral%20linen%20backdrop%2C%20editorial%20dessert%20photography%2C%20high%20detail&width=800&height=600&seq=food-creme-brulee-01&orientation=landscape",
    gallery: [
      "https://readdy.ai/api/search-image?query=creme%20brulee%20in%20a%20white%20ramekin%20with%20cracked%20caramelized%20sugar%20crust%20and%20berries%2C%20soft%20natural%20light%2C%20warm%20neutral%20linen%20backdrop%2C%20editorial%20dessert%20photography%2C%20high%20detail&width=800&height=600&seq=food-creme-brulee-01&orientation=landscape",
      "https://readdy.ai/api/search-image?query=close%20up%20spoon%20breaking%20the%20caramelized%20top%20of%20creme%20brulee%2C%20warm%20light%2C%20cream%20table%20surface%2C%20editorial%20dessert%20photography%2C%20high%20detail&width=800&height=600&seq=food-creme-brulee-02&orientation=landscape",
      "https://readdy.ai/api/search-image?query=elegant%20french%20creme%20brulee%20dessert%20on%20a%20marble%20surface%2C%20warm%20ambient%20light%2C%20beige%20backdrop%2C%20editorial%20dessert%20photography%2C%20high%20detail&width=800&height=600&seq=food-creme-brulee-03&orientation=landscape",
    ],
    short: "现烤脆焦糖壳，勺子一敲就碎的绵密布丁",
    description:
      "香草荚与淡奶油慢火熬煮，低温水浴烤至细腻滑嫩，上桌前撒糖现烤出琥珀色脆壳。轻轻敲开，焦糖的苦甜与布丁的奶香在舌尖交融，是甜点里最治愈的一道。",
    tags: ["甜点", "布丁", "法式"],
    specs: [
      { label: "口味", options: [{ name: "原味香草", price: 0 }, { name: "抹茶", price: 4 }, { name: "咖啡", price: 4 }] },
      { label: "点缀", options: [{ name: "莓果", price: 0 }, { name: "加冰淇淋球", price: 8 }] },
    ],
    restaurant: { name: "Patisserie 甜屋", phone: "400-880-1440", deliveryTime: "约 30 分钟", distance: "距您 1.4km" },
    nutrition: [
      { label: "热量", value: "320 kcal" },
      { label: "蛋白质", value: "6 g" },
      { label: "碳水", value: "32 g" },
      { label: "脂肪", value: "18 g" },
    ],
  },
  {
    id: "matcha-mille",
    name: "抹茶千层蛋糕",
    category: "甜点",
    categoryId: "dessert",
    price: 38,
    rating: 4.9,
    sales: 2100,
    distance: 1.7,
    image:
      "https://readdy.ai/api/search-image?query=slice%20of%20matcha%20mille%20crepe%20cake%20with%20many%20thin%20layers%20on%20a%20white%20plate%2C%20soft%20natural%20light%2C%20warm%20neutral%20linen%20backdrop%2C%20editorial%20dessert%20photography%2C%20high%20detail&width=800&height=600&seq=food-matcha-mille-01&orientation=landscape",
    gallery: [
      "https://readdy.ai/api/search-image?query=slice%20of%20matcha%20mille%20crepe%20cake%20with%20many%20thin%20layers%20on%20a%20white%20plate%2C%20soft%20natural%20light%2C%20warm%20neutral%20linen%20backdrop%2C%20editorial%20dessert%20photography%2C%20high%20detail&width=800&height=600&seq=food-matcha-mille-01&orientation=landscape",
      "https://readdy.ai/api/search-image?query=close%20up%20of%20green%20tea%20mille%20crepe%20cake%20layers%20dust%20with%20matcha%20powder%2C%20warm%20light%2C%20cream%20table%20surface%2C%20editorial%20dessert%20photography%2C%20high%20detail&width=800&height=600&seq=food-matcha-mille-02&orientation=landscape",
      "https://readdy.ai/api/search-image?query=whole%20matcha%20mille%20crepe%20cake%20on%20a%20cake%20stand%2C%20warm%20ambient%20light%2C%20beige%20backdrop%2C%20editorial%20dessert%20photography%2C%20high%20detail&width=800&height=600&seq=food-matcha-mille-03&orientation=landscape",
    ],
    short: "十八层薄可丽饼，宇治抹茶与奶油层层叠叠",
    description:
      "手工摊制十八层可丽饼，每一层抹上宇治抹茶奶油，冷藏定型后切块。入口是茶香四溢的微苦回甘，与醇厚奶油交织，层次分明，抹茶控绝对绕不开的一款。",
    tags: ["抹茶", "千层", "网红"],
    specs: [
      { label: "尺寸", options: [{ name: "单块", price: 0 }, { name: "4 寸整只", price: 68 }, { name: "6 寸整只", price: 128 }] },
      { label: "口味", options: [{ name: "经典抹茶", price: 0 }, { name: "抹茶红豆", price: 4 }] },
    ],
    restaurant: { name: "Patisserie 甜屋", phone: "400-880-1440", deliveryTime: "约 30 分钟", distance: "距您 1.7km" },
    nutrition: [
      { label: "热量", value: "400 kcal" },
      { label: "蛋白质", value: "8 g" },
      { label: "碳水", value: "44 g" },
      { label: "脂肪", value: "22 g" },
    ],
  },
  {
    id: "lava-cake",
    name: "巧克力熔岩蛋糕",
    category: "甜点",
    categoryId: "dessert",
    price: 34,
    rating: 4.7,
    sales: 1420,
    distance: 2.0,
    image:
      "https://readdy.ai/api/search-image?query=chocolate%20lava%20cake%20with%20molten%20center%20flowing%20out%20on%20a%20white%20plate%20with%20berries%2C%20soft%20natural%20light%2C%20warm%20neutral%20linen%20backdrop%2C%20editorial%20dessert%20photography%2C%20high%20detail&width=800&height=600&seq=food-lava-cake-01&orientation=landscape",
    gallery: [
      "https://readdy.ai/api/search-image?query=chocolate%20lava%20cake%20with%20molten%20center%20flowing%20out%20on%20a%20white%20plate%20with%20berries%2C%20soft%20natural%20light%2C%20warm%20neutral%20linen%20backdrop%2C%20editorial%20dessert%20photography%2C%20high%20detail&width=800&height=600&seq=food-lava-cake-01&orientation=landscape",
      "https://readdy.ai/api/search-image?query=close%20up%20of%20a%20spoon%20dipping%20into%20molten%20dark%20chocolate%20center%2C%20warm%20light%2C%20cream%20table%20surface%2C%20editorial%20dessert%20photography%2C%20high%20detail&width=800&height=600&seq=food-lava-cake-02&orientation=landscape",
      "https://readdy.ai/api/search-image?query=warm%20chocolate%20fondant%20dessert%20with%20powdered%20sugar%2C%20warm%20ambient%20light%2C%20beige%20backdrop%2C%20editorial%20dessert%20photography%2C%20high%20detail&width=800&height=600&seq=food-lava-cake-03&orientation=landscape",
    ],
    short: "切开流心，70% 黑巧克力在舌尖慢慢融化",
    description:
      "外层是微苦的 70% 黑巧克力蛋糕体，内里包裹着流动的巧克力熔浆，现烤现出。切开的一瞬间，浓郁的可可香气扑面而来，搭配一勺香草冰淇淋，冷热交织绝了。",
    tags: ["巧克力", "流心", "现烤"],
    specs: [
      { label: "搭配", options: [{ name: "原味", price: 0 }, { name: "加香草冰淇淋", price: 8 }, { name: "加莓果", price: 6 }] },
    ],
    restaurant: { name: "Patisserie 甜屋", phone: "400-880-1440", deliveryTime: "约 32 分钟", distance: "距您 2.0km" },
    nutrition: [
      { label: "热量", value: "460 kcal" },
      { label: "蛋白质", value: "7 g" },
      { label: "碳水", value: "48 g" },
      { label: "脂肪", value: "26 g" },
    ],
  },
  {
    id: "grapefruit-soda",
    name: "鲜萃西柚气泡水",
    category: "饮品",
    categoryId: "drink",
    price: 22,
    rating: 4.6,
    sales: 1980,
    distance: 0.8,
    image:
      "https://readdy.ai/api/search-image?query=glass%20of%20fresh%20grapefruit%20sparkling%20soda%20with%20ice%20and%20mint%20on%20a%20light%20wooden%20table%2C%20soft%20natural%20light%2C%20warm%20neutral%20backdrop%2C%20editorial%20drink%20photography%2C%20high%20detail&width=800&height=600&seq=food-grapefruit-soda-01&orientation=landscape",
    gallery: [
      "https://readdy.ai/api/search-image?query=glass%20of%20fresh%20grapefruit%20sparkling%20soda%20with%20ice%20and%20mint%20on%20a%20light%20wooden%20table%2C%20soft%20natural%20light%2C%20warm%20neutral%20backdrop%2C%20editorial%20drink%20photography%2C%20high%20detail&width=800&height=600&seq=food-grapefruit-soda-01&orientation=landscape",
      "https://readdy.ai/api/search-image?query=close%20up%20of%20bubbles%20rising%20in%20a%20pink%20grapefruit%20drink%20with%20citrus%20slice%2C%20warm%20light%2C%20cream%20surface%2C%20editorial%20drink%20photography%2C%20high%20detail&width=800&height=600&seq=food-grapefruit-soda-02&orientation=landscape",
      "https://readdy.ai/api/search-image?query=iced%20citrus%20soda%20with%20grapefruit%20segments%20in%20a%20tall%20glass%2C%20warm%20ambient%20light%2C%20beige%20backdrop%2C%20editorial%20drink%20photography%2C%20high%20detail&width=800&height=600&seq=food-grapefruit-soda-03&orientation=landscape",
    ],
    short: "现榨红心西柚，气泡在舌尖炸开的清爽",
    description:
      "新鲜红心西柚现榨，保留果肉颗粒，加入冰镇气泡水与少许蜂蜜，酸酸甜甜、气泡绵密。吃多了油腻食物后来一杯，瞬间清爽解腻。",
    tags: ["气泡水", "西柚", "零负担"],
    specs: [
      { label: "甜度", options: [{ name: "无糖", price: 0 }, { name: "三分糖", price: 0 }, { name: "标准糖", price: 0 }] },
      { label: "冰量", options: [{ name: "正常冰", price: 0 }, { name: "少冰", price: 0 }, { name: "去冰", price: 0 }] },
    ],
    restaurant: { name: "Sip 鲜饮研究所", phone: "400-880-1466", deliveryTime: "约 24 分钟", distance: "距您 0.8km" },
    nutrition: [
      { label: "热量", value: "120 kcal" },
      { label: "蛋白质", value: "1 g" },
      { label: "碳水", value: "28 g" },
      { label: "脂肪", value: "0 g" },
    ],
  },
  {
    id: "mango-cheese",
    name: "手打芝士芒芒",
    category: "饮品",
    categoryId: "drink",
    price: 26,
    rating: 4.8,
    sales: 2560,
    distance: 1.0,
    image:
      "https://readdy.ai/api/search-image?query=cup%20of%20mango%20cheese%20tea%20with%20milk%20foam%20top%20and%20fresh%20mango%20chunks%2C%20soft%20natural%20light%2C%20warm%20neutral%20backdrop%2C%20editorial%20drink%20photography%2C%20high%20detail&width=800&height=600&seq=food-mango-cheese-01&orientation=landscape",
    gallery: [
      "https://readdy.ai/api/search-image?query=cup%20of%20mango%20cheese%20tea%20with%20milk%20foam%20top%20and%20fresh%20mango%20chunks%2C%20soft%20natural%20light%2C%20warm%20neutral%20backdrop%2C%20editorial%20drink%20photography%2C%20high%20detail&width=800&height=600&seq=food-mango-cheese-01&orientation=landscape",
      "https://readdy.ai/api/search-image?query=close%20up%20of%20thick%20salted%20cheese%20foam%20on%20golden%20mango%20smoothie%2C%20warm%20light%2C%20cream%20surface%2C%20editorial%20drink%20photography%2C%20high%20detail&width=800&height=600&seq=food-mango-cheese-02&orientation=landscape",
      "https://readdy.ai/api/search-image?query=fresh%20mango%20dessert%20drink%20with%20cheese%20foam%20in%20a%20clear%20cup%2C%20warm%20ambient%20light%2C%20beige%20backdrop%2C%20editorial%20drink%20photography%2C%20high%20detail&width=800&height=600&seq=food-mango-cheese-03&orientation=landscape",
    ],
    short: "台农芒果现打，厚厚芝士奶盖一勺到底",
    description:
      "选用当季台农芒果，现点现打成果泥，铺上厚厚一层手打海盐芝士奶盖。先抿一口咸香奶盖，再拌匀喝下，芒果的清甜与芝士的浓香完美融合，一口入魂。",
    tags: ["奶盖", "芒果", "爆款"],
    specs: [
      { label: "甜度", options: [{ name: "三分糖", price: 0 }, { name: "五分糖", price: 0 }, { name: "标准糖", price: 0 }] },
      { label: "冰量", options: [{ name: "正常冰", price: 0 }, { name: "少冰", price: 0 }] },
    ],
    restaurant: { name: "Sip 鲜饮研究所", phone: "400-880-1466", deliveryTime: "约 24 分钟", distance: "距您 1.0km" },
    nutrition: [
      { label: "热量", value: "260 kcal" },
      { label: "蛋白质", value: "4 g" },
      { label: "碳水", value: "46 g" },
      { label: "脂肪", value: "8 g" },
    ],
  },
  {
    id: "osmanthus-latte",
    name: "冷萃桂花拿铁",
    category: "饮品",
    categoryId: "drink",
    price: 30,
    rating: 4.7,
    sales: 1240,
    distance: 1.5,
    image:
      "https://readdy.ai/api/search-image?query=glass%20of%20cold%20brew%20osmanthus%20latte%20with%20milk%20swirl%20and%20tiny%20yellow%20osmanthus%20flowers%2C%20soft%20natural%20light%2C%20warm%20neutral%20backdrop%2C%20editorial%20drink%20photography%2C%20high%20detail&width=800&height=600&seq=food-osmanthus-latte-01&orientation=landscape",
    gallery: [
      "https://readdy.ai/api/search-image?query=glass%20of%20cold%20brew%20osmanthus%20latte%20with%20milk%20swirl%20and%20tiny%20yellow%20osmanthus%20flowers%2C%20soft%20natural%20light%2C%20warm%20neutral%20backdrop%2C%20editorial%20drink%20photography%2C%20high%20detail&width=800&height=600&seq=food-osmanthus-latte-01&orientation=landscape",
      "https://readdy.ai/api/search-image?query=close%20up%20of%20layered%20coffee%20and%20milk%20with%20osmanthus%20petals%2C%20warm%20light%2C%20cream%20surface%2C%20editorial%20drink%20photography%2C%20high%20detail&width=800&height=600&seq=food-osmanthus-latte-02&orientation=landscape",
      "https://readdy.ai/api/search-image?query=iced%20latte%20with%20osmanthus%20syrup%20in%20a%20clear%20glass%2C%20warm%20ambient%20light%2C%20beige%20backdrop%2C%20editorial%20drink%20photography%2C%20high%20detail&width=800&height=600&seq=food-osmanthus-latte-03&orientation=landscape",
    ],
    short: "十二小时冷萃咖啡，秋日限定的桂花香气",
    description:
      "阿拉比卡豆冷萃十二小时，口感顺滑不酸涩，加入现熬桂花糖浆与冰鲜牛奶。桂花的甜香在咖啡里若隐若现，像把整个秋天装进杯子里。",
    tags: ["咖啡", "桂花", "冷萃"],
    specs: [
      { label: "奶底", options: [{ name: "全脂奶", price: 0 }, { name: "燕麦奶", price: 4 }, { name: "零脂奶", price: 2 }] },
      { label: "甜度", options: [{ name: "无糖", price: 0 }, { name: "半糖", price: 0 }] },
    ],
    restaurant: { name: "Sip 鲜饮研究所", phone: "400-880-1466", deliveryTime: "约 26 分钟", distance: "距您 1.5km" },
    nutrition: [
      { label: "热量", value: "180 kcal" },
      { label: "蛋白质", value: "6 g" },
      { label: "碳水", value: "24 g" },
      { label: "脂肪", value: "6 g" },
    ],
  },
  {
    id: "plum-drink",
    name: "古法酸梅汤",
    category: "饮品",
    categoryId: "drink",
    price: 18,
    rating: 4.5,
    sales: 1180,
    distance: 2.3,
    image:
      "https://readdy.ai/api/search-image?query=glass%20of%20traditional%20chinese%20sour%20plum%20drink%20with%20ice%20and%20dried%20osmanthus%2C%20dark%20amber%20color%2C%20soft%20natural%20light%2C%20warm%20neutral%20backdrop%2C%20editorial%20drink%20photography%2C%20high%20detail&width=800&height=600&seq=food-plum-drink-01&orientation=landscape",
    gallery: [
      "https://readdy.ai/api/search-image?query=glass%20of%20traditional%20chinese%20sour%20plum%20drink%20with%20ice%20and%20dried%20osmanthus%2C%20dark%20amber%20color%2C%20soft%20natural%20light%2C%20warm%20neutral%20backdrop%2C%20editorial%20drink%20photography%2C%20high%20detail&width=800&height=600&seq=food-plum-drink-01&orientation=landscape",
      "https://readdy.ai/api/search-image?query=close%20up%20of%20a%20pitcher%20of%20dark%20sour%20plum%20beverage%20with%20floating%20ice%2C%20warm%20light%2C%20cream%20surface%2C%20editorial%20drink%20photography%2C%20high%20detail&width=800&height=600&seq=food-plum-drink-02&orientation=landscape",
      "https://readdy.ai/api/search-image?query=chinese%20summer%20drink%20suanmeitang%20in%20a%20ceramic%20cup%2C%20warm%20ambient%20light%2C%20beige%20backdrop%2C%20editorial%20drink%20photography%2C%20high%20detail&width=800&height=600&seq=food-plum-drink-03&orientation=landscape",
    ],
    short: "乌梅山楂古法熬煮，消暑开胃的老味道",
    description:
      "乌梅、山楂、陈皮、甘草与桂花慢火熬煮两小时，冷藏后饮用，酸甜回甘、生津止渴。不加任何香精，是小时候夏天最熟悉的那一口老味道。",
    tags: ["酸梅汤", "古法", "消暑"],
    specs: [
      { label: "规格", options: [{ name: "中杯", price: 0 }, { name: "大杯", price: 6 }] },
      { label: "冰量", options: [{ name: "正常冰", price: 0 }, { name: "少冰", price: 0 }, { name: "常温", price: 0 }] },
    ],
    restaurant: { name: "老街糖水铺", phone: "400-880-1488", deliveryTime: "约 34 分钟", distance: "距您 2.3km" },
    nutrition: [
      { label: "热量", value: "90 kcal" },
      { label: "蛋白质", value: "0 g" },
      { label: "碳水", value: "22 g" },
      { label: "脂肪", value: "0 g" },
    ],
  },
];

export const getFoodById = (id: string): Food | undefined => foods.find((f) => f.id === id);

export const getFoodsByCategory = (categoryId: string): Food[] =>
  foods.filter((f) => f.categoryId === categoryId);